import type { DraftState } from '../../../../layout/sidebar/encounter-summary'
import type { ModalState } from '../delivery-history/delivery-history-form'
import type { CurrentPregnancy } from './types'
import type { CSSProperties, FormEvent } from 'react'

import cx from 'classnames'
import { differenceInDays, format } from 'date-fns'
import pluralize from 'pluralize'
import { useMemo, useEffect, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useDebounceValue } from 'usehooks-ts'

import { PlainButton } from '../../../../shared/Button'
import * as Card from '../../../../shared/card'
import {
    Button,
    Checkbox,
    DraftButton,
    PortedModal
} from '../../../../shared/NarusMaterial'
import { useWarnings } from '../components/delivered-context'
import { Input } from '../components/input'

import {
    useCurrentPregnancySubmitMutation,
    useCurrentPregnancy,
    useCurrentPregnancyDeleteDraft,
    useCurrentPregnancyDraftMutation
} from './queries'
import { currentPregnancySchema } from './types'

let inputStyles: CSSProperties = {
    visibility: 'visible',
    fontSize: '12px',
    cursor: 'text',
    lineHeight: '14px',
    letterSpacing: '0.5px',
    color: '#6e6c6f'
}

let timer: NodeJS.Timeout

function debounce<T>(
    callback: (...args: T[]) => Promise<void>,
    delay: number
): (...args: T[]) => Promise<void> {
    return async function (...rest) {
        clearTimeout(timer)
        timer = setTimeout(() => {
            callback(...rest).catch((e) => console.error(e))
        }, delay)
    }
}

function getEDC(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
        return null
    }

    let date = new Date(value).setHours(0, 0, 0, 0)

    let edcDate = new Date(+date + 40 * 7 * 24 * 60 * 60 * 1000)

    if (isNaN(edcDate.getTime())) {
        return null
    }

    return format(edcDate, 'MM/dd/yyyy')
}

function getDuration(value: string | null | undefined): string | null {
    if (typeof value !== 'string') {
        return null
    }

    let date = new Date(value)

    if (isNaN(date.getTime())) {
        return null
    }

    let diff = differenceInDays(new Date(), date)

    let days = diff % 7
    let weeks = Math.floor(diff / 7)

    if (weeks >= 42) {
        days = 0
        weeks = 42
    }

    let weeksString = pluralize('week', weeks, true)
    let daysString = pluralize('day', days, true)

    return `${weeksString} ${daysString}`
}

export function CurrentPregnancyForm(): JSX.Element {
    let { id } = useParams()
    let { show } = useWarnings()

    let [draft, setDraft] = useState<DraftState>({ getting: true })
    let submitMutation = useCurrentPregnancySubmitMutation(id, () =>
        setDraft({ get: true })
    )
    let draftMutation = useCurrentPregnancyDraftMutation(id, () =>
        setDraft({ set: true })
    )
    let deleteDraftMutation = useCurrentPregnancyDeleteDraft(id, () =>
        setDraft({ get: true })
    )
    let currentPregnancy = useCurrentPregnancy(id)

    useEffect(() => {
        currentPregnancy.isFetched && setDraft({ get: true })
    }, [currentPregnancy.isFetched])

    let saveDraft = useCallback(
        async (data?: Partial<CurrentPregnancy> | undefined): Promise<void> => {
            let previousData = currentPregnancy.data?.data

            if (
                currentPregnancy.data?.data.lmp === data?.lmp &&
                currentPregnancy.data?.data.delivered === data?.delivered
            ) {
                return
            }

            setDraft({ setting: true })

            draftMutation.mutate({ ...data, id: previousData?.id })
        },
        [draftMutation.mutate, id, currentPregnancy.data?.data]
    )

    let debouncedSaveDraft = debounce(saveDraft, 500)

    const setData = useCallback(
        (values: Partial<CurrentPregnancy>): void => {
            void debouncedSaveDraft(values)
        },
        [debouncedSaveDraft]
    )

    let [debouncedDraft] = useDebounceValue(draft, 500)

    let data = currentPregnancy.data?.data

    let onSubmit = useCallback(
        (data: Partial<CurrentPregnancy>): void => {
            setDraft({ publishing: true })

            const parsed = currentPregnancySchema.safeParse(data)

            if (parsed.success) {
                if (parsed.data.delivered) {
                    show(
                        'delivery-history',
                        'Add information about the delivery of the current pregnancy'
                    )
                    show(
                        'obstetrical-history',
                        'Add information about the delivery of the current pregnancy'
                    )
                }

                submitMutation.mutate({
                    ...parsed.data,
                    id: currentPregnancy.data?.data?.id
                })
            }
        },
        [currentPregnancy.data?.data?.id, show, submitMutation.mutate]
    )

    if (!currentPregnancy.isFetched) {
        return <div />
    }

    return (
        <Card.Card>
            <Card.Header expandable>
                <h2>Current Pregnancy</h2>
            </Card.Header>

            <Card.ExpandableWrapper>
                <Form
                    data={data}
                    setData={setData}
                    isDraft={currentPregnancy.data?.draft ?? false}
                    deleteDraft={() => {
                        deleteDraftMutation.mutate(null)
                        void currentPregnancy?.refetch()
                    }}
                    submit={onSubmit}
                    draft={debouncedDraft}
                    loading={
                        !!draft.setting || !!draft.getting || !!draft.publishing
                    }
                />
            </Card.ExpandableWrapper>
        </Card.Card>
    )
}

export interface FormProps {
    data?: Partial<CurrentPregnancy>
    setData: (values: Partial<CurrentPregnancy>) => void
    isDraft: boolean
    submit: (state: Partial<CurrentPregnancy>) => void
    draft: DraftState
    loading: boolean
    deleteDraft: () => void
}

export function Form({
    data,
    setData,
    isDraft,
    submit,
    draft,
    loading,
    deleteDraft
}: FormProps): JSX.Element {
    let [isValid, setIsValid] = useState<boolean>()
    let [modal, setModal] = useState<ModalState>()
    let [state, setState] = useState<Partial<CurrentPregnancy>>(data ?? {})

    let [debouncedLoading] = useDebounceValue(loading, 500)

    useEffect(() => {
        setIsValid(() => currentPregnancySchema.safeParse(state).success)

        setData(state)
    }, [state])

    useEffect(() => {
        setState(data ?? {})
    }, [data])

    function handleSubmit(e: FormEvent): void {
        e.preventDefault()
        submit(state)
    }

    let onDiscard = useCallback((): void => {
        setModal({
            title: 'Are you sure you want to cancel all changes done on this form?',
            open: true,
            actions: [
                <Button
                    key={1}
                    data-cy="modal_cancel-btn"
                    label="Cancel"
                    onClick={() => {
                        setModal(undefined)
                    }}
                    grey
                />,
                <Button
                    key={2}
                    label="Discard"
                    data-cy="modal_discard-btn"
                    onClick={() => {
                        deleteDraft()
                        setModal(undefined)
                    }}
                />
            ]
        })
    }, [deleteDraft])

    let edc = useMemo(() => getEDC(state?.lmp), [state.lmp])
    let duration = useMemo(() => getDuration(state?.lmp), [state.lmp])

    return (
        <form
            onSubmit={handleSubmit}
            className="px-8"
            id="current-pregnancy-form"
        >
            <div className="flex flex-row gap-20 py-8 flex-wrap border-b border-x-0 border-t-0 border-solid border-gray-200">
                <div className="flex flex-row gap-6 flex-wrap">
                    <Input
                        data-cy="lmp-input"
                        value={state?.lmp ?? ''}
                        onChange={(e) => {
                            setState({
                                ...state,
                                lmp: e.target.value,
                                delivered: false
                            })
                        }}
                        label="LMP"
                        title="Last menstrual period"
                        required={!state?.delivered}
                        type="date"
                        max={new Date().toISOString().substring(0, 10)}
                        inputStyle={{ width: '110px' }}
                    />
                    <Input
                        label="EDC"
                        disabled
                        type="text"
                        title="Estimated date of confinement"
                        auto
                        value={edc ?? 'mm/dd/yyyy'}
                        inputStyle={{ width: '110px', color: '#6E6C6F' }}
                    />
                </div>

                <div className="flex flex-row gap-10 flex-wrap ">
                    <div className="flex flex-col gap-1">
                        <label
                            htmlFor="duration"
                            style={inputStyles}
                            className="relative w-fit flex flex-row"
                        >
                            Pregnancy Duration
                        </label>
                        <div
                            id="duration"
                            className={cx(
                                'flex flex-col flex-1 justify-center text-base',
                                !duration && 'text-gray-500'
                            )}
                        >
                            {duration ?? 'Specify LMP'}
                        </div>
                    </div>

                    <div className="self-end pb-3.5">
                        <Checkbox
                            value={state?.delivered ?? false}
                            onChange={(value) => {
                                setState({
                                    ...state,
                                    delivered: value,
                                    lmp: undefined
                                })
                            }}
                            label="Delivered"
                        />
                    </div>
                </div>
            </div>

            <div className="flex flex-row gap-2 justify-end py-6 px-8">
                <DraftButton draft={draft} />
                <PlainButton
                    disabled={!isDraft || debouncedLoading}
                    label="Discard Changes"
                    type="button"
                    onClick={onDiscard}
                    data-cy="discard-btn"
                />
                <PlainButton
                    disabled={!isValid || !isDraft || debouncedLoading}
                    color="primary"
                    btnType="contained"
                    label="Publish"
                    type="submit"
                    data-cy="current-pregnancy-publish-btn"
                />
            </div>
            <PortedModal
                open={modal?.open}
                title={modal?.title}
                actions={modal?.actions}
            />
        </form>
    )
}
